import { useState, useEffect, useContext, useMemo } from "react";
import { CircularProgress, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import FilterListIcon from "@mui/icons-material/FilterList";
import styles from "./styles.module.css";
import OnlineOrderContext from "context/OnlineOrderContext";
import OnlineOrderDetailsPopup from "components/popups/OnlineOrderDetailsPopup/OnlineOrderDetailsPopup";
import ShopContext from "context/ShopContext";
import dateTimeHelpers from "helpers/DateTimeHelpers";
import { ONLINE_ORDER_STATUS, ONLINE_ORDER_SOURCES, ONLINE_ORDER_FILTER_TABS } from "const";

const OnlineOrdersPage = () => {
  const { selectedShop } = useContext(ShopContext);
  const { onlineOrders, loading, fetchOnlineOrders } = useContext(OnlineOrderContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(ONLINE_ORDER_STATUS.ALL);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const currencySymbol = selectedShop?.currency_symbol || "₹";

  useEffect(() => {
    if (onlineOrders === null) {
      fetchOnlineOrders();
    }
  }, [onlineOrders, fetchOnlineOrders]);

  const handleOpenDetails = (orderId) => {
    setSelectedOrderId(orderId);
  };

  const handleCloseDetails = () => {
    setSelectedOrderId(null);
  };

  const handleOrderProcessed = () => {};

  const filteredOrders = useMemo(() => {
    return onlineOrders?.filter((order) => {
      // Status filter
      const status = (order.status || ONLINE_ORDER_STATUS.PENDING).toUpperCase();
      if (activeFilter !== ONLINE_ORDER_STATUS.ALL && status !== activeFilter.toUpperCase()) {
        return false;
      }

      // Search term filter
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const idMatch = order.id?.toString().includes(term);
      const customerName = (order.details?.customer?.name || order.customer?.name || "").toLowerCase();
      const customerMobile = (order.details?.customer?.mobile || order.customer?.mobile || "").toLowerCase();
      const sourceMatch = (order.source || "").toLowerCase().includes(term);

      return idMatch || customerName.includes(term) || customerMobile.includes(term) || sourceMatch;
    });
  }, [onlineOrders, activeFilter, searchTerm]);

  // Summary counts
  const stats = useMemo(() => {
    let pending = 0;
    let delivered = 0;
    let totalAmount = 0;

    for (const order of onlineOrders || []) {
      const status = (order.status || ONLINE_ORDER_STATUS.PENDING).toUpperCase();
      if (status === ONLINE_ORDER_STATUS.PENDING) {
        pending += 1;
      } else if (status === ONLINE_ORDER_STATUS.DELIVERED) {
        delivered += 1;
      }
      const amt = Number(order.online_payment?.amount || order.details?.amount || 0);
      totalAmount += isNaN(amt) ? 0 : amt;
    }

    return { total: onlineOrders?.length, pending, delivered, totalAmount };
  }, [onlineOrders]);

  const getSourceBadge = (source) => {
    const src = (source || ONLINE_ORDER_SOURCES.SANOFT_CART).toLowerCase();
    if (src === ONLINE_ORDER_SOURCES.SWIGGY) {
      return <span className={`${styles.sourcePill} ${styles.swiggy}`}>Swiggy</span>;
    }
    if (src === ONLINE_ORDER_SOURCES.ZOMATO) {
      return <span className={`${styles.sourcePill} ${styles.zomato}`}>Zomato</span>;
    }
    return <span className={`${styles.sourcePill} ${styles.sanoftCart}`}>Sanoft Cart</span>;
  };

  const getStatusBadge = (status) => {
    const st = (status || ONLINE_ORDER_STATUS.PENDING).toUpperCase();
    switch (st) {
      case ONLINE_ORDER_STATUS.PENDING:
        return <span className={`${styles.statusPill} ${styles.pending}`}>Pending</span>;
      case ONLINE_ORDER_STATUS.PREPARING:
        return <span className={`${styles.statusPill} ${styles.preparing}`}>Preparing</span>;
      case ONLINE_ORDER_STATUS.READY:
        return <span className={`${styles.statusPill} ${styles.ready}`}>Ready</span>;
      case ONLINE_ORDER_STATUS.DELIVERED:
        return <span className={`${styles.statusPill} ${styles.delivered}`}>Delivered</span>;
      case ONLINE_ORDER_STATUS.CANCELLED:
        return <span className={`${styles.statusPill} ${styles.cancelled}`}>Cancelled</span>;
      default:
        return <span className={`${styles.statusPill} ${styles.default}`}>{st}</span>;
    }
  };

  const formatOrderTime = (isoString) => {
    if (!isoString) return;
    try {
      return dateTimeHelpers.formatDateTime(isoString);
    } catch {
      return isoString;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div className={styles.iconBox}>
            <ShoppingBagIcon className={styles.headerIcon} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Online Orders</h1>
            <p className={styles.pageSubtitle}>Manage incoming orders from Sanoft Cart, Swiggy, and Zomato</p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <IconButton
            className={styles.refreshButton}
            onClick={() => fetchOnlineOrders(true)}
            disabled={loading}
            title="Refresh Orders"
          >
            <RefreshIcon className={`${styles.refreshIcon} ${loading ? styles.spinning : ""}`} />
          </IconButton>
        </div>
      </div>

      {/* Metrics Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}>
            <ShoppingBagIcon fontSize="small" />
          </div>
          <div>
            <div className={styles.statLabel}>Total Orders</div>
            <div className={styles.statValue}>{stats.total}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ backgroundColor: "#fef3c7", color: "#d97706" }}>
            <PendingActionsIcon fontSize="small" />
          </div>
          <div>
            <div className={styles.statLabel}>Pending Orders</div>
            <div className={styles.statValue} style={{ color: "#d97706" }}>
              {stats.pending}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIconWrapper} style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}>
            <CheckCircleOutlineIcon fontSize="small" />
          </div>
          <div>
            <div className={styles.statLabel}>Delivered Orders</div>
            <div className={styles.statValue} style={{ color: "#16a34a" }}>
              {stats.delivered}
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className={styles.filterSection}>
        <div className={styles.tabsWrapper}>
          {ONLINE_ORDER_FILTER_TABS.map((tab) => {
            const count =
              tab.id === ONLINE_ORDER_STATUS.ALL
                ? onlineOrders?.length
                : onlineOrders?.filter(
                    (o) => (o.status || ONLINE_ORDER_STATUS.PENDING).toUpperCase() === tab.id.toUpperCase(),
                  ).length;

            return (
              <button
                key={tab.id}
                className={`${styles.tabBtn} ${activeFilter === tab.id ? styles.activeTabBtn : ""}`}
                onClick={() => setActiveFilter(tab.id)}
              >
                <span>{tab.label}</span>
                <span className={styles.tabCount}>{count}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.searchBox}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by Order ID, customer, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button className={styles.clearSearchBtn} onClick={() => setSearchTerm("")}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Orders Grid */}
      <div className={styles.contentArea}>
        {loading && onlineOrders?.length === 0 ? (
          <div className={styles.loadingBox}>
            <CircularProgress color="error" size={44} />
            <p className={styles.loadingText}>Fetching online orders...</p>
          </div>
        ) : filteredOrders?.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIllustration}>
              <FilterListIcon className={styles.emptyIcon} />
            </div>
            <h3 className={styles.emptyTitle}>No online orders found</h3>
            <p className={styles.emptyDesc}>
              {searchTerm || activeFilter !== ONLINE_ORDER_STATUS.ALL
                ? "Try clearing filters or search terms to see more orders."
                : "New incoming online orders will appear here automatically."}
            </p>
            <button className={styles.primaryBtn} onClick={() => fetchOnlineOrders(true)}>
              <RefreshIcon fontSize="small" /> Refresh Orders
            </button>
          </div>
        ) : (
          <div className={styles.ordersGrid}>
            {filteredOrders?.map((order) => {
              const customerName = order.details?.customer?.name || order.customer?.name || "Walk-in Customer";
              const customerMobile = order.details?.customer?.mobile || order.customer?.mobile;
              const tableName = order.details?.customer?.table;
              const items = order.details?.items || [];
              const amount = order.online_payment?.amount || order.details?.amount || 0;
              const isPending = (order.status || "").toUpperCase() === ONLINE_ORDER_STATUS.PENDING;

              return (
                <div
                  key={order.id}
                  className={`${styles.orderCard} ${isPending ? styles.pendingOrderCard : ""}`}
                  onClick={() => handleOpenDetails(order.id)}
                >
                  {/* Card Header */}
                  <div className={styles.cardHeader}>
                    <div className={styles.orderIdGroup}>
                      <span className={styles.orderIdText}>Order #{order.id}</span>
                      {getSourceBadge(order.source)}
                    </div>
                    {getStatusBadge(order.status)}
                  </div>

                  {/* Customer Info */}
                  <div className={styles.customerRow}>
                    <div className={styles.customerDetail}>
                      <PersonIcon className={styles.subIcon} />
                      <span className={styles.customerName}>{customerName}</span>
                    </div>
                    {customerMobile && (
                      <div className={styles.customerDetail}>
                        <PhoneIcon className={styles.subIcon} />
                        <span>{customerMobile}</span>
                      </div>
                    )}
                    {tableName && (
                      <div className={styles.customerDetail}>
                        <span style={{ fontWeight: 600, color: "#e50218" }}>Table: {tableName}</span>
                      </div>
                    )}
                  </div>

                  {/* Items Preview */}
                  <div className={styles.itemsPreview}>
                    <div className={styles.itemsHeader}>
                      <span>Items ({items.length})</span>
                      <span className={styles.amountHighlight}>
                        {currencySymbol} {Number(amount).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Timestamp & Action */}
                  <div className={styles.cardFooter}>
                    <div className={styles.timeInfo}>
                      <AccessTimeIcon className={styles.subIcon} />
                      <span>{formatOrderTime(order.created_at || order.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Details Popup */}
      {selectedOrderId && (
        <OnlineOrderDetailsPopup
          open={Boolean(selectedOrderId)}
          orderId={selectedOrderId}
          onClose={handleCloseDetails}
          onOrderProcessed={handleOrderProcessed}
        />
      )}
    </div>
  );
};

export default OnlineOrdersPage;
